import { CreateTaskRequestDTO, TaskResponseDTO } from 'typings/dto/task';
import DateUtils from 'utils/Date';
import DateWithTimezone from 'utils/implementations/DateWithTimezone';
import Mapper from './Mapper';
import DIContainer from 'services/DIContainer';
import { USER_POSITION } from 'typings/models/user.enum';
import TaskUtils from 'utils/models/TaskUtils';

export default class TaskMapper extends Mapper<Task, TaskResponseDTO> {
  constructor(private dateUtils: DateUtils, private subEntitiesMappers: DIContainer.SubEntitiesMappers) {
    super({
      closedAt: 'closedAt',
      deadline: null,
      description: 'description',
      groupRecipient: 'groupRecipients',
      priority: 'priority',
      status: 'state',
      title: 'name',
      type: 'type',
      userRecipients: 'userRecipients',
      customFieldRecipients: 'customFieldRecipients',
      closedByUser: 'closedBy',
      createdByUser: 'createdBy',
      timezone: null,
      linkedEntities: 'linkedEntities',
      attachments: 'attachments',
      onSubmitAction: 'onSubmitAction',
    });
  }

  public responseDTOToModel(dto: TaskResponseDTO): Task {
    const groupRecipients = dto.groupRecipient.map((g) => ({
      ownerId: g.tenantId,
      technicalName: g.groupName,
      roles: g.roles,
    }));

    if (!dto.customFieldRecipients) dto.customFieldRecipients = [];
    const customFieldRecipients = dto.customFieldRecipients.flatMap((c) => {
      const position = (TaskUtils.getPositionByCustomFieldTechName(c) as USER_POSITION) || null;
      return position ? [{ technicalName: c, position }] : [];
    });

    const linkedEntities = dto.linkedEntities.map((linkedEntity) => ({
      ...linkedEntity,
      data: linkedEntity.data
        ? {
            ...linkedEntity.data,
            attachments:
              'attachments' in linkedEntity.data
                ? linkedEntity.data.attachments.map(this.subEntitiesMappers.file.responseDTOToModel_temp)
                : undefined,
          }
        : undefined,
    }));

    return {
      ...this.responseDTOToModelViaDiffMap(dto),
      expiresAt: dto.deadline?.expiresAt ? new DateWithTimezone(dto.deadline.expiresAt, dto.timezone, this.dateUtils) : undefined,
      startsAt: dto.deadline?.startsAt ? new DateWithTimezone(dto.deadline.startsAt, dto.timezone, this.dateUtils) : undefined,
      closedAt: dto.closedAt ? new DateWithTimezone(dto.closedAt, dto.timezone, this.dateUtils) : undefined,
      groupRecipients,
      linkedEntities,
      customFieldRecipients: customFieldRecipients,
      attachments: dto.attachments ? dto.attachments.map(this.subEntitiesMappers.file.responseDTOToModel_temp) : [],
    };
  }

  public modelToRequestDTO(model: Task, orderId: string): CreateTaskRequestDTO {
    const groupRecipient = model.groupRecipients.map((g) => ({
      groupName: g.technicalName,
      tenantId: g.ownerId,
      roles: g.roles,
    }));
    const startsAt = model.startsAt?.getRawDate().toISOString();
    const expiresAt = model.expiresAt?.getRawDate().toISOString();

    const { name, description, linkedEntities, state, type, priority } = model;

    return {
      title: name,
      description,
      linkedEntities,
      priority,
      type,
      orderId,
      status: state,
      userRecipientsIds: model.userRecipients.map((u) => u.id),
      groupRecipient,
      customFieldRecipients: model.customFieldRecipients.map((c) => c.technicalName),
      deadline: { startsAt, expiresAt },
      closedAt: model.closedAt?.getRawDate().toISOString(),
      closedByUserId: model.closedBy?.id,
      attachments: model.attachments.map(this.subEntitiesMappers.file.fsFileResponseDTOToMainApiRequestDTO_temp),
      onSubmitAction: model.onSubmitAction,
    };
  }
}
